import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { ModalContext } from '@/utils/contexts/ModalContext';

import { dataActions } from '@/store/actions/data.actions';

import api from '@/services/api';
import { authActions } from '@/store/actions/auth.actions';
import { saveAs } from 'file-saver';

import InitialSortTypeEnum from '@/utils/constants/InitialSortTypeEnum';
import { setOrdInQuerySearchParams } from '@/utils/functions/setOrdInQuerySearchParams';
import { checkInitialParams } from '@/utils/functions/checkInitialParams';
import useWindowIsMobile from '@/utils/hooks/useWindowIsMobile';
import { CheckPermission } from '@/utils/permissions/permissions';

import { Divider } from '@mui/material';
import MenuUI from '@/views/components/Header/MainMenu/MenuUI/MenuUI';
import MenuItemUI from '@/views/components/Header/MainMenu/MenuItemUI/MenuItemUI';

import styles from '@/views/components/Header/Header.module.scss';

const MainMenu = () => {
	const { MODIFIED_ON: { value: ORD_MODIFIED_ON }, INPUT_PHRASE: { value: ORD_INPUT_PHRASE }, NAME: { value: ORD_NAME }, SURNAME: { value: ORD_SURNAME } } = InitialSortTypeEnum;
	const dispatch = useDispatch();

	const { showModal } = useContext(ModalContext);

	const { menuResources } = useSelector(state => state.data);
	
	const { dict, ppj } = useSelector((state) => state.app);
	const user = useSelector((state) => state.auth.user);

	const userRole = user && user.role;
	const isReaderUser = userRole && userRole === 'reader';
	const isExternalAccountUser = user && user.provider !== null;
	const isMimReaderUser = user && (isExternalAccountUser && isReaderUser);

	const tMenuHeader = dict.header.menu;
	const tSimpleTextLinks = tMenuHeader.simpleTextLinks;
	const tHowToUseService = tMenuHeader.howToUseService;
	const tAdvices = tMenuHeader.simpleTextAdvices;
	const tMyAccount = tMenuHeader.myAccount;
	const { tDownloadInstruction, tShowOnboarding } = tHowToUseService.options;
	const { tUserList, tDepartmentList, tReport, tTemplateManagement, tChangePassword, tLogout } = tMyAccount.options;
	const { tFindAdvice, tMyQuestions, tAskQuestion, tAdmin } = tAdvices.options;

	const { pathname } = useLocation();

	const isMobile = useWindowIsMobile(991);

	const [ collapsed, setCollapsed ] = useState(false);

	useEffect(() => {
		setCollapsed(false);
	}, [ isMobile ]);

	useEffect(() => {
		if (!menuResources) { dispatch(dataActions.getMenuResources()); }
		if (user.displayOnboarding) {
			openOnboarding();
		}
	}, []);

	const menuLinkValues = [
		{
			menuLinkId: 'letters',
			menuLinkName: `letters?${checkInitialParams(ORD_MODIFIED_ON, location.search, user)}`,
			menuLinksSecondary: [ 'letter', 'add-letter' ],
			noPadding: true,
		},
		{
			menuLinkId: 'templates',
			menuLinkName: `templates?${checkInitialParams(ORD_MODIFIED_ON, location.search, user)}`,
			menuLinksSecondary: [ 'template', 'create-template', 'edit-template' ],
		},
		{
			menuLinkId: 'simpleText',
			menuLinkName: 'simple-text',
		},
		{
			menuLinkId: 'dict',
			menuLinkName: `dict${setOrdInQuerySearchParams(ORD_INPUT_PHRASE)}`,
		},
	];

	const simpleTextAdvicesValues = {
		findAdvice: {
			label: tFindAdvice,
			link: `simple-text-advices`,
		},
		adminAdvices: {
			label: tAdmin,
			link: `simple-text-advices-admin`,
		},
		myQuestions: {
			label: tMyQuestions,
			link: `simple-text-my-advices`,
		},
		askQuestion: {
			label: tAskQuestion,
			link: `simple-text-my-advices?question`,
		},
	};

	const myAccountLinkValues = {
		userList: {
			label: tUserList,
			link: `users${setOrdInQuerySearchParams(ORD_SURNAME)}`,
		},
		departmentList: {
			label: tDepartmentList,
			link: `departments${setOrdInQuerySearchParams(ORD_NAME)}`,
		},
		report: {
			label: tReport,
			link: `generate-report`,
		},
		templatesManagement: {
			label: tTemplateManagement,
			link: `templates-management`,
		},
		changePassword: {
			label: tChangePassword,
			link: 'change-password',
		},
	};

	const getPathName = (pathValue) => pathname.split("/")[1] === pathValue;

	const getGeneratedMainMenuLink = (linkId, linkValue, linkValuesSecondary) => {
		let classNameValue = styles["header__menu--btn"];

		if (getPathName(linkValue.split('?')[0])) {
			classNameValue = `${styles["header__menu--btn"]} ${styles["active"]}`;
		} else if (linkValuesSecondary && linkValuesSecondary.some((value) => { return getPathName(value.split('?')[0]); })) {
			classNameValue = `${styles["header__menu--btn"]} ${styles["active"]}`;
		}

		return (
			<Link
				key={ linkId }
				to={ `/${linkValue}` }
				className={ classNameValue }
			>
				{ tMenuHeader[linkId] }
			</Link>
		);
	};
	
	const getCompletedMimLogout = () => {
		api.post('/oauth2/logout').then((response) => {
			if (response.status === 200) {
				dispatch(authActions.signOut(false));
				const oidcSessionId = sessionStorage.getItem("oidcSessionId");
				if (oidcSessionId) {
					let redirectUrl = window.location.protocol + '//' + window.location.hostname; 
					if (window.location.port) { redirectUrl += (':' + window.location.port); }
					redirectUrl += "/";
					const finalUrl = `${response.headers['custom-location']}?post_logout_redirect_uri=${encodeURIComponent(redirectUrl)}&id_token_hint=${oidcSessionId}`;
					window.location.href = finalUrl;
				} else {
					window.location.href = response.headers['custom-location'];
				}	
			}
		});
	};

	const getGeneratedLogoutBtn = (btnLabel, isMenuItem) => (
		<div
			role='button'
			tabIndex={ 0 }
			onClick={ isExternalAccountUser ? getCompletedMimLogout : () => dispatch(authActions.signOut()) }
			className={ isMenuItem ? '' : styles['header__menu--btn'] }
		>
			{ btnLabel }
		</div>
	);

	const getResourceDownload = async (resourceName) => {
		await api.get(`/resources/documentation/${resourceName}`, { responseType: 'blob' })
			.then(({headers, data}) => {
				let pdf = new Blob([ data ], {type: 'application/pdf'});
				let pdfURL = URL.createObjectURL(pdf);
				let fileName = headers["content-disposition"].split("filename=")[1];
				fileName = fileName.replaceAll('"','');
				saveAs(data, fileName);
				window.open(pdfURL, '_blank');
			});
	};

	const openOnboarding = () => {
		showModal({
			componentName: 'ModalOnboarding',
		});
	};

	const getGeneratedSimpleTextLinksMenuItemLink = (resource) => (
		<MenuItemUI
			className={ styles['header__menu--item'] }
			onClick={ handleSimpleTextLinksClose }
			key={ resource.type }
		>
			{resource.link ? (
				<a
					href={ resource.link }
					target="_blank"
					rel="noreferrer"
				>
					{ resource.label }&nbsp;
					<i
						className='icon-external'
						style={ {fontSize: "16px"} }
					/>
				</a>
			) : (
				<div
					role="button"
					tabIndex={ 0 }
					onClick={ () => getResourceDownload(resource.type) }
				>
					{ resource.label }&nbsp;
					<i
						className='icon-download'
						style={ {fontSize: "16px"} }
					/>
				</div>
			)}
		</MenuItemUI>
	);

	const getGeneratedAdviceMenuItemLink = (dictItemValue, selected, reload = false) => (
		<MenuItemUI
			className={ styles['header__menu--item'] }
			onClick={ handleAdviceClose }
			style={ selected ? { backgroundColor: '#F0F7FF' } : {} }
		>
			<Link 
				to={ `/${dictItemValue.link}` }
				style={ selected ? { fontWeight: '500' } : {} }
				reloadDocument={ reload }
			>
				{ selected && <i className='icon-accept' /> }
				{ dictItemValue.label }
			</Link>
		</MenuItemUI>
	);

	const getGeneratedMyAccountMenuItemLink = (dictItemValue, selected) => (
		<MenuItemUI
			className={ styles['header__menu--item'] }
			onClick={ handleMyAccountClose }
			style={ selected ? { backgroundColor: '#F0F7FF' } : {} }
		>
			<Link 
				to={ `/${dictItemValue.link}` }
				style={ selected ? { fontWeight: '500' } : {} }
			>
				{ selected && <i className='icon-accept' /> }
				{ dictItemValue.label }
			</Link>
		</MenuItemUI>
	);

	const [ simpleTextLinksAnchorEl, setSimpleTextLinksAnchorEl ] = useState(null);
	const handleSimpleTextLinksClick = (event) => setSimpleTextLinksAnchorEl(event.currentTarget);
	const handleSimpleTextLinksClose = () => setSimpleTextLinksAnchorEl(null);

	const [ howToUseServiceAnchorEl, setHowToUseServiceAnchorEl ] = useState(null);
	const handleHowToUseServiceClick = (event) => setHowToUseServiceAnchorEl(event.currentTarget);
	const handleHowToUseServiceClose = () => setHowToUseServiceAnchorEl(null);

	const [ myAccountAnchorEl, setMyAccountAnchorEl ] = useState(null);
	const handleMyAccountClick = (event) => setMyAccountAnchorEl(event.currentTarget);
	const handleMyAccountClose = () => setMyAccountAnchorEl(null);

	const [ adviceAnchorEl, setAdviceAnchorEl ] = useState(null);
	const handleAdviceClick = (event) => setAdviceAnchorEl(event.currentTarget);
	const handleAdviceClose = () => setAdviceAnchorEl(null);

	const getInfoDownload = async () => {
		await api.get('/resources/documentation', { responseType: 'blob' })
			.then(({headers, data}) => {
				let pdf = new Blob([ data ], {type: 'application/pdf'});
				let pdfURL = URL.createObjectURL(pdf);
				let fileName = headers["content-disposition"].split("filename=")[1];
				fileName = fileName.replaceAll('"','');
				saveAs(data, fileName);
				window.open(pdfURL, '_blank');
			});
	};

	const isAnyAdviceItemOpen = () => {
		const path = pathname.split("/")[1];
		return path === 'simple-text-advices' || path === 'simple-text-advices-admin' || path === 'simple-text-advice' || path === 'simple-text-my-advices';
	};

	const isAnyMyMenuItemOpen = () => {
		const path = pathname.split("/")[1];
		return path === 'users' || path === 'departments' || path === 'change-password' || path === 'generate-report' || path === 'templates-management';
	};

	const isSpecificMenuItemOpen = (name) => {
		const path = pathname.split("/")[1];
		return path === name;
	};

	return (
		<>
			<button
				className={ styles['header__hamburger'] }
				onClick={ () => setCollapsed(state => !state) }
			>
				<i className={ `icon-${!collapsed ? 'hamburger' : 'close'} icon` } />
				<div className={ styles['header__hamburger--text'] }>MENU</div>
			</button>
			<menu className={ collapsed ? styles['header__menu--collapsed'] : styles['header__menu'] }>
				<div className={ styles['header__menu--box'] }>
					{user && (
						<div className={ styles['header__menu--container'] }>
							<div className={ styles['header__menu--user'] }>
								{ tMenuHeader.userWelcome } { user.forename } { user.surname }
							</div>
							{!collapsed && <Divider
								className={ styles['header__menu--user'] }
								orientation="vertical"
								flexItem
								component='li'
								sx={ {color: "#E9E9E9", height: "12px", alignSelf: 'center'} }
							/>}
							<div
								className={ styles['header__menu--info'] }
								role='button'
								tabIndex={ 0 }
								onClick={ simpleTextLinksAnchorEl ? handleSimpleTextLinksClose : handleSimpleTextLinksClick }
							>
								{ tSimpleTextLinks.mainLabel } <div className={ styles['header__menu--btn-icon'] }><i className={ `icon-arrow-${simpleTextLinksAnchorEl ? 'up' : 'down'}-full` } /></div>
							</div>

							{!collapsed ? (
								<MenuUI
									className={ styles['header__menu--main-container'] }
									anchorEl={ simpleTextLinksAnchorEl }
									open={ Boolean(simpleTextLinksAnchorEl) }
									onClose={ handleSimpleTextLinksClose }
								>
									{menuResources && menuResources.map((resource) => getGeneratedSimpleTextLinksMenuItemLink(resource))}
								</MenuUI>
							) : (
								<>{simpleTextLinksAnchorEl && menuResources && menuResources.map((resource) => getGeneratedSimpleTextLinksMenuItemLink(resource))}</>
							)}
							{!collapsed && <Divider
								className={ styles['header__menu--user'] }
								orientation="vertical"
								flexItem
								component='li'
								sx={ {color: "#E9E9E9", height: "12px", alignSelf: 'center'} }
							/>}
							<div
								className={ styles['header__menu--info'] }
								role='button'
								tabIndex={ 0 }
								onClick={ howToUseServiceAnchorEl ? handleHowToUseServiceClose : handleHowToUseServiceClick }
							>
								{ tHowToUseService.mainLabel } <div className={ styles['header__menu--btn-icon'] }><i className={ `icon-arrow-${howToUseServiceAnchorEl ? 'up' : 'down'}-full` } /></div>
							</div>

							{!collapsed ? (
								<MenuUI
									className={ styles['header__menu--main-container'] }
									anchorEl={ howToUseServiceAnchorEl }
									open={ Boolean(howToUseServiceAnchorEl) }
									onClose={ handleHowToUseServiceClose }
								>
									<MenuItemUI
										className={ styles['header__menu--item'] }
										onClick={ handleHowToUseServiceClose }
									>
										<div
											role="button"
											tabIndex={ 0 }
											onClick={ () => openOnboarding() }
										>
											{ tShowOnboarding }&nbsp;
										</div>
									</MenuItemUI>
									<MenuItemUI
										className={ styles['header__menu--item'] }
										onClick={ handleHowToUseServiceClose }
									>
										<div
											role="button"
											tabIndex={ 0 }
											onClick={ () => getInfoDownload() }
										>
											{ tDownloadInstruction }&nbsp;
											<i
												className='icon-download'
												style={ {fontSize: "16px"} }
											/>
										</div>
									</MenuItemUI>
								</MenuUI>
							) : (
								<>
									{howToUseServiceAnchorEl && <>
										<MenuItemUI
											className={ styles['header__menu--item'] }
											onClick={ handleHowToUseServiceClose }
										>
											<div
												role="button"
												tabIndex={ 0 }
												onClick={ () => openOnboarding() }
											>
												{ tShowOnboarding }&nbsp;
											</div>
										</MenuItemUI>
										<MenuItemUI
											className={ styles['header__menu--item'] }
											onClick={ handleHowToUseServiceClose }
										>
											<div
												role="button"
												tabIndex={ 0 }
												onClick={ () => getInfoDownload() }
											>
												{ tDownloadInstruction }&nbsp;
												<i
													className='icon-download'
													style={ {fontSize: "16px"} }
												/>
											</div>
										</MenuItemUI>
									</>}
								</>
							)}
						</div>
					)}
					{collapsed && <Divider sx={ { margin: "16px 0px" } }/>}
					<div className={ isMobile ? styles['header__menu--mobile-buttons-wrapper'] : styles['header__menu--desktop-buttons-wrapper'] }>
						{ menuLinkValues.map(({ menuLinkId, menuLinkName, menuLinksSecondary }) => getGeneratedMainMenuLink(menuLinkId, menuLinkName, menuLinksSecondary)) }
					
						{ppj?.enabled && <div
							role='button'
							tabIndex={ 0 }
							className={ isAnyAdviceItemOpen() ? `${styles['header__menu--btn']} ${styles['active']}` : styles['header__menu--btn'] }
							onClick={ adviceAnchorEl ? handleAdviceClose : handleAdviceClick }
						>
							{ tAdvices.mainLabel } <div className={ styles['header__menu--btn-icon'] }><i className={ `icon-arrow-${adviceAnchorEl ? 'up' : 'down'}-full` } /></div>
						</div>}
						{!collapsed ? (
							<MenuUI
								className={ styles['header__menu--main-container'] }
								anchorEl={ adviceAnchorEl }
								open={ Boolean(adviceAnchorEl) }
								onClose={ handleAdviceClose }
							>
								{ getGeneratedAdviceMenuItemLink(simpleTextAdvicesValues.findAdvice, isSpecificMenuItemOpen('simple-text-advices')) }
								{ CheckPermission('canManageQuestions', userRole) && getGeneratedAdviceMenuItemLink(simpleTextAdvicesValues.adminAdvices, isSpecificMenuItemOpen('simple-text-advices-admin')) }
								{ CheckPermission('canAskQuestions', userRole) && getGeneratedAdviceMenuItemLink(simpleTextAdvicesValues.myQuestions, isSpecificMenuItemOpen('simple-text-my-advices')) }
								{ CheckPermission('canAskQuestions', userRole) && <Divider
									flexItem
									component='li'
								/>}
								{ CheckPermission('canAskQuestions', userRole) && getGeneratedAdviceMenuItemLink(simpleTextAdvicesValues.askQuestion) }

							</MenuUI>
						) : (
							<>
								{adviceAnchorEl && <>
									{ getGeneratedAdviceMenuItemLink(simpleTextAdvicesValues.findAdvice, isSpecificMenuItemOpen('simple-text-advices')) }
									{ CheckPermission('canManageQuestions', userRole) && getGeneratedAdviceMenuItemLink(simpleTextAdvicesValues.adminAdvices, isSpecificMenuItemOpen('simple-text-advices-admin')) }
									{ CheckPermission('canAskQuestions', userRole) && getGeneratedAdviceMenuItemLink(simpleTextAdvicesValues.myQuestions, isSpecificMenuItemOpen('simple-text-my-advices')) }
									{ CheckPermission('canAskQuestions', userRole) && getGeneratedAdviceMenuItemLink(simpleTextAdvicesValues.askQuestion, undefined, true) }
								</>}
							</>
						)}

						{
							user && !isMimReaderUser ?
								<>
									<div
										role='button'
										tabIndex={ 0 }
										className={ isAnyMyMenuItemOpen() ? `${ styles['header__menu--btn'] } ${ styles['active'] }` : styles['header__menu--btn'] }
										onClick={ myAccountAnchorEl ? handleMyAccountClose : handleMyAccountClick }
									>
										{ tMyAccount.mainLabel } <div className={ styles['header__menu--btn-icon'] }><i className={ `icon-arrow-${myAccountAnchorEl ? 'up' : 'down'}-full` } /></div>
									</div>
									{!collapsed ? (
										<MenuUI
											className={ styles['header__menu--main-container'] }
											anchorEl={ myAccountAnchorEl }
											open={ Boolean(myAccountAnchorEl) }
											onClose={ handleMyAccountClose }
										>
											{ CheckPermission('canOpenUserManagement', userRole) &&
											getGeneratedMyAccountMenuItemLink(myAccountLinkValues.userList, isSpecificMenuItemOpen('users')) }

											{ CheckPermission('canOpenDepartmentManagement', userRole) && getGeneratedMyAccountMenuItemLink(myAccountLinkValues.departmentList, isSpecificMenuItemOpen('departments')) }

											{ CheckPermission('canOpenReportGeneration', userRole) && 
												getGeneratedMyAccountMenuItemLink(myAccountLinkValues.report, isSpecificMenuItemOpen('generate-report')) }

											{ CheckPermission('canOpenTemplateManagement', userRole) && 
												getGeneratedMyAccountMenuItemLink(myAccountLinkValues.templatesManagement, isSpecificMenuItemOpen('templates-management')) }

											{!isExternalAccountUser && (
												CheckPermission('canOpenUserManagement', userRole) || CheckPermission('canOpenDepartmentManagement', userRole) || CheckPermission('canOpenReportGeneration', userRole) || CheckPermission('canOpenTemplateManagement', userRole)
											) && <Divider
												flexItem
												component='li'
											/>}

											{!isExternalAccountUser && getGeneratedMyAccountMenuItemLink(myAccountLinkValues.changePassword, isSpecificMenuItemOpen('change-password')) }

											<MenuItemUI className={ styles['header__menu--item'] }>
												{ getGeneratedLogoutBtn(tLogout, 'isMenuItem') }
											</MenuItemUI>
										</MenuUI>
									) : (
										<>
											{myAccountAnchorEl && <>
												{ CheckPermission('canOpenUserManagement', userRole) &&
												getGeneratedMyAccountMenuItemLink(myAccountLinkValues.userList, isSpecificMenuItemOpen('users')) }

												{ CheckPermission('canOpenDepartmentManagement', userRole) && getGeneratedMyAccountMenuItemLink(myAccountLinkValues.departmentList, isSpecificMenuItemOpen('departments')) }

												{ CheckPermission('canOpenReportGeneration', userRole) &&
												getGeneratedMyAccountMenuItemLink(myAccountLinkValues.report, isSpecificMenuItemOpen('generate-report')) }

												{ CheckPermission('canOpenTemplateManagement', userRole) &&
												getGeneratedMyAccountMenuItemLink(myAccountLinkValues.templatesManagement, isSpecificMenuItemOpen('templates-management')) }

												{!isExternalAccountUser && getGeneratedMyAccountMenuItemLink(myAccountLinkValues.changePassword, isSpecificMenuItemOpen('change-password')) }

												<MenuItemUI className={ styles['header__menu--item'] }>
													{ getGeneratedLogoutBtn(tLogout, 'isMenuItem') }
												</MenuItemUI>
											</>}
										</>
									)}
								</> : null
						}
						{ isMimReaderUser ? getGeneratedLogoutBtn(tMenuHeader.logout) : null }
					</div>
				</div>
			</menu>
		</>
	);
};
export default MainMenu;
